import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, PlayIcon } from '../icons.js';
import { apiGet } from '../../api/client.js';
import { specColor, vfmt } from '../../lib/video-format.js';

/**
 * Atoms for the Training Videos surface: VideoThumb, VideoCard, VideoRow.
 *
 * VideoThumb — 16:9 tile with a gradient background keyed off the
 * specialisation, a duration pill, optional progress bar, NEW / Watched
 * badges, and a hover play affordance. Real keyframe thumbs are fetched
 * lazily from `/api/v1/videos/:id/poster` when the tile mounts; the
 * gradient is the placeholder so the layout never shifts.
 *
 * VideoCard — VideoThumb + spec/series eyebrow + title + presenter line.
 * Wraps the whole thing in a Link so the row strips can be keyboard-
 * navigated and right-click "open in new tab" works as expected.
 *
 * VideoRow — horizontal scroll strip with arrow buttons. Used for
 * Continue Watching, each series row, and Related Videos on the detail
 * page. Hides the scrollbar (`no-scrollbar` utility in index.css).
 */

export interface VideoCardData {
  id: string;
  title: string;
  durationSeconds?: number;
  presenter?: { name: string; role?: string };
  seriesId?: string;
  episode?: number;
  totalEpisodes?: number;
  // Optional derived/UX fields
  progress?: number; // 0..1
  isNew?: boolean;
  views?: number;
  specId?: string;
  specLabel?: string;
}

interface VideoThumbProps {
  video: VideoCardData;
  size?: 's' | 'm' | 'l' | 'xl';
  showProgress?: boolean;
  onClick?: () => void;
  /** When true, do NOT fetch the poster image (keeps unit tests deterministic). */
  noPoster?: boolean;
}

const THUMB_HEIGHTS: Record<NonNullable<VideoThumbProps['size']>, number> = {
  s: 96,
  m: 156,
  l: 220,
  xl: 360,
};

/** Inline SVG gradient placeholder keyed off the specialisation. */
function gradientBg(specId: string | undefined): string {
  const c = specColor(specId);
  // Slight perceived darkening for the bottom-right corner.
  return `linear-gradient(135deg, ${c} 0%, #0A1730 100%)`;
}

export function VideoThumb({
  video,
  size = 'm',
  showProgress = true,
  onClick,
  noPoster = false,
}: VideoThumbProps): JSX.Element {
  const [hover, setHover] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const aborted = useRef(false);

  useEffect(() => {
    if (noPoster) return;
    aborted.current = false;
    (async () => {
      try {
        const data = await apiGet<{ url: string }>(`/api/v1/videos/${video.id}/poster`);
        if (!aborted.current && data?.url) setPosterUrl(data.url);
      } catch {
        // 404 (keyframe not yet) or unauth — gradient placeholder stays.
      }
    })();
    return () => {
      aborted.current = true;
    };
  }, [video.id, noPoster]);

  const h = THUMB_HEIGHTS[size];
  const pct =
    showProgress && video.progress && video.progress > 0 ? Math.round(video.progress * 100) : null;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        width: '100%',
        height: h,
        borderRadius: 'var(--ov-radius-sm, 8px)',
        overflow: 'hidden',
        background: posterUrl
          ? `#172E57 url("${posterUrl}") center/cover no-repeat`
          : gradientBg(video.specId),
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform var(--ov-dur-fast, 150ms)',
        transform: hover && onClick ? 'translateY(-1px)' : 'none',
      }}
    >
      {/* Bottom gradient veil for legibility */}
      <div
        style={{
          position: 'absolute',
          inset: 'auto 0 0 0',
          height: '50%',
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />
      {video.isNew && (
        <span
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            padding: '3px 8px',
            borderRadius: 999,
            background: '#fff',
            color: 'var(--ov-primary, #172E57)',
            font: '700 10px/1 var(--ov-font-ui, system-ui)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          New
        </span>
      )}
      {video.progress !== undefined && video.progress >= 1 && (
        <span
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            padding: '3px 8px',
            borderRadius: 999,
            background: 'var(--ov-success, #018236)',
            color: '#fff',
            font: '700 10px/1 var(--ov-font-ui, system-ui)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <CheckIcon className="w-3 h-3" />
          Watched
        </span>
      )}
      <span
        style={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          padding: '2px 8px',
          borderRadius: 4,
          background: 'rgba(14,12,20,0.78)',
          color: '#fff',
          font: '600 11px/1.4 var(--ov-font-ui, system-ui)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.02em',
        }}
      >
        {vfmt(video.durationSeconds ?? 0)}
      </span>
      {hover && onClick && (
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.94)',
            color: 'var(--ov-primary, #172E57)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
          }}
        >
          <PlayIcon className="w-7 h-7" />
        </span>
      )}
      {pct !== null && pct < 100 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'rgba(255,255,255,0.18)',
          }}
        >
          <div style={{ width: `${pct}%`, height: '100%', background: '#fff' }} />
        </div>
      )}
    </div>
  );
}

interface VideoCardProps {
  video: VideoCardData;
  size?: VideoThumbProps['size'];
}

export function VideoCard({ video, size = 'm' }: VideoCardProps): JSX.Element {
  return (
    <Link
      to={`/videos/${video.id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: 'transparent',
        borderRadius: 'var(--ov-radius-sm, 8px)',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <VideoThumb video={video} size={size} />
      <div style={{ padding: '0 2px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            font: '600 10.5px/14px var(--ov-font-ui, system-ui)',
            color: 'var(--ov-fg-4, #828282)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 4,
          }}
        >
          {video.specId && (
            <span
              style={{ width: 8, height: 8, borderRadius: 2, background: specColor(video.specId) }}
            />
          )}
          {video.specLabel && <span>{video.specLabel}</span>}
          {video.episode && video.totalEpisodes && (
            <>
              <span>·</span>
              <span
                style={{
                  textTransform: 'none',
                  letterSpacing: 0,
                  font: '500 11.5px/14px var(--ov-font-body, system-ui)',
                }}
              >
                Ep {video.episode} of {video.totalEpisodes}
              </span>
            </>
          )}
        </div>
        <div
          style={{
            font: '600 14px/19px var(--ov-font-ui, system-ui)',
            color: 'var(--ov-fg-1, #0E0C14)',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {video.title}
        </div>
        <div
          style={{
            font: '400 12px/16px var(--ov-font-body, system-ui)',
            color: 'var(--ov-fg-3, #4F4F4F)',
          }}
        >
          {video.presenter?.name ?? ''}
          {video.presenter?.name && video.views !== undefined ? ' · ' : ''}
          {video.views !== undefined ? `${video.views} views` : ''}
        </div>
      </div>
    </Link>
  );
}

interface VideoRowProps {
  title: string;
  subtitle?: string;
  videos: VideoCardData[];
  action?: React.ReactNode;
}

export function VideoRow({ title, subtitle, videos, action }: VideoRowProps): JSX.Element | null {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (dir: -1 | 1): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };
  if (!videos.length) return null;
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 10 }}>
        <div>
          <div
            style={{
              font: '700 16px/20px var(--ov-font-ui, system-ui)',
              color: 'var(--ov-fg-1, #0E0C14)',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                font: '400 12px/16px var(--ov-font-body, system-ui)',
                color: 'var(--ov-fg-3, #4F4F4F)',
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {action}
        <button onClick={() => scroll(-1)} aria-label="Scroll left" style={rowNavBtnStyle}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            chevron_left
          </span>
        </button>
        <button onClick={() => scroll(1)} aria-label="Scroll right" style={rowNavBtnStyle}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            chevron_right
          </span>
        </button>
      </div>
      <div
        ref={scrollRef}
        className="no-scrollbar"
        style={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: 'minmax(240px, 260px)',
          gap: 14,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          paddingBottom: 4,
        }}
      >
        {videos.map((v) => (
          <VideoCard key={v.id} video={v} />
        ))}
      </div>
    </section>
  );
}

const rowNavBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  background: '#fff',
  border: '1px solid var(--ov-border-soft, #E5E5E5)',
  color: 'var(--ov-fg-2, #1D1D1D)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
